import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid3X3, Maximize2, Minus, Plus } from 'lucide-react';
import type { Point, StoryElement } from '../types';
import { useEditorStore } from '../store/editor';
import { getCanvasSize } from '../model/factories';
import { ElementArt, SceneArt } from '../render/Artwork';

interface MoveInteraction {
  mode: 'move';
  pointerId: number;
  start: Point;
  initial: Map<string, { x: number; y: number; width: number; height: number }>;
  ids: string[];
  changed: boolean;
}

interface ResizeInteraction {
  mode: 'resize';
  pointerId: number;
  start: Point;
  initial: StoryElement;
  handle: ResizeHandle;
  changed: boolean;
}

interface RotateInteraction {
  mode: 'rotate';
  pointerId: number;
  center: Point;
  startAngle: number;
  initialRotation: number;
  id: string;
  changed: boolean;
}

type Interaction = MoveInteraction | ResizeInteraction | RotateInteraction;
type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const handles: Array<{ key: ResizeHandle; x: number; y: number; cursor: string }> = [
  { key: 'nw', x: 0, y: 0, cursor: 'nwse-resize' },
  { key: 'n', x: 0.5, y: 0, cursor: 'ns-resize' },
  { key: 'ne', x: 1, y: 0, cursor: 'nesw-resize' },
  { key: 'e', x: 1, y: 0.5, cursor: 'ew-resize' },
  { key: 'se', x: 1, y: 1, cursor: 'nwse-resize' },
  { key: 's', x: 0.5, y: 1, cursor: 'ns-resize' },
  { key: 'sw', x: 0, y: 1, cursor: 'nesw-resize' },
  { key: 'w', x: 0, y: 0.5, cursor: 'ew-resize' },
];

const rotatePoint = (x: number, y: number, degrees: number): Point => {
  const angle = (degrees * Math.PI) / 180;
  return { x: x * Math.cos(angle) - y * Math.sin(angle), y: x * Math.sin(angle) + y * Math.cos(angle) };
};

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
};

export const CanvasEditor: React.FC = () => {
  const project = useEditorStore((state) => state.project);
  const activeCellId = useEditorStore((state) => state.activeCellId);
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const zoom = useEditorStore((state) => state.zoom);
  const setZoom = useEditorStore((state) => state.setZoom);
  const setSelectedIds = useEditorStore((state) => state.setSelectedIds);
  const toggleSelected = useEditorStore((state) => state.toggleSelected);
  const mutate = useEditorStore((state) => state.mutate);
  const startInteraction = useEditorStore((state) => state.startInteraction);
  const finishInteraction = useEditorStore((state) => state.finishInteraction);
  const cancelInteraction = useEditorStore((state) => state.cancelInteraction);
  const deleteSelected = useEditorStore((state) => state.deleteSelected);
  const duplicateSelected = useEditorStore((state) => state.duplicateSelected);
  const copySelected = useEditorStore((state) => state.copySelected);
  const pasteClipboard = useEditorStore((state) => state.pasteClipboard);
  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);
  const moveLayer = useEditorStore((state) => state.moveLayer);
  const viewportRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const interactionRef = useRef<Interaction | null>(null);
  const [fitScale, setFitScale] = useState(0.6);
  const [guides, setGuides] = useState<{ x: boolean; y: boolean }>({ x: false, y: false });

  const activeCell = project?.cells.find((cell) => cell.id === activeCellId) ?? project?.cells[0];
  const size = project ? getCanvasSize(project.layout.aspectRatio) : { width: 1600, height: 900 };
  const elements = useMemo(() => [...(activeCell?.elements ?? [])].sort((a, b) => a.zIndex - b.zIndex), [activeCell?.elements]);
  const selectedElements = useMemo(() => elements.filter((element) => selectedIds.includes(element.id)), [elements, selectedIds]);
  const single = selectedElements.length === 1 ? selectedElements[0] : null;

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const update = () => {
      const bounds = viewport.getBoundingClientRect();
      const next = Math.min((bounds.width - 120) / size.width, (bounds.height - 120) / size.height);
      setFitScale(Math.max(0.12, next));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [size.height, size.width]);

  const svgPoint = useCallback((clientX: number, clientY: number): Point => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const matrix = svg.getScreenCTM();
    if (!matrix) return { x: 0, y: 0 };
    const transformed = point.matrixTransform(matrix.inverse());
    return { x: transformed.x, y: transformed.y };
  }, []);

  const startMove = (event: React.PointerEvent<SVGGElement>, element: StoryElement) => {
    event.stopPropagation();
    if (!project || !activeCell || element.locked) {
      setSelectedIds([element.id]);
      return;
    }
    let ids: string[];
    if (event.shiftKey) {
      toggleSelected(element.id);
      ids = selectedIds.includes(element.id) ? selectedIds.filter((id) => id !== element.id) : [...selectedIds, element.id];
      if (!ids.includes(element.id)) return;
    } else if (selectedIds.includes(element.id)) {
      ids = selectedIds;
    } else {
      ids = [element.id];
      setSelectedIds(ids);
    }
    const initial = new Map<string, { x: number; y: number; width: number; height: number }>();
    activeCell.elements.filter((item) => ids.includes(item.id)).forEach((item) => initial.set(item.id, { x: item.x, y: item.y, width: item.width, height: item.height }));
    startInteraction();
    interactionRef.current = { mode: 'move', pointerId: event.pointerId, start: svgPoint(event.clientX, event.clientY), initial, ids, changed: false };
    svgRef.current?.setPointerCapture(event.pointerId);
  };

  const startResize = (event: React.PointerEvent<SVGCircleElement>, element: StoryElement, handle: ResizeHandle) => {
    event.stopPropagation();
    if (element.locked) return;
    startInteraction();
    interactionRef.current = { mode: 'resize', pointerId: event.pointerId, start: svgPoint(event.clientX, event.clientY), initial: structuredClone(element), handle, changed: false };
    svgRef.current?.setPointerCapture(event.pointerId);
  };

  const startRotate = (event: React.PointerEvent<SVGCircleElement>, element: StoryElement) => {
    event.stopPropagation();
    if (element.locked) return;
    const center = { x: element.x + element.width / 2, y: element.y + element.height / 2 };
    const point = svgPoint(event.clientX, event.clientY);
    startInteraction();
    interactionRef.current = {
      mode: 'rotate',
      pointerId: event.pointerId,
      center,
      startAngle: Math.atan2(point.y - center.y, point.x - center.x),
      initialRotation: element.rotation,
      id: element.id,
      changed: false,
    };
    svgRef.current?.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const interaction = interactionRef.current;
    if (!interaction || !project || !activeCell || interaction.pointerId !== event.pointerId) return;
    const point = svgPoint(event.clientX, event.clientY);
    interaction.changed = true;

    if (interaction.mode === 'move') {
      const dx = point.x - interaction.start.x;
      const dy = point.y - interaction.start.y;
      let showX = false;
      let showY = false;
      mutate((draft) => {
        const cell = draft.cells.find((item) => item.id === activeCell.id);
        if (!cell) return;
        cell.elements.forEach((element) => {
          const initial = interaction.initial.get(element.id);
          if (!initial || element.locked) return;
          let x = initial.x + dx;
          let y = initial.y + dy;
          const centerX = x + element.width / 2;
          const centerY = y + element.height / 2;
          if (Math.abs(centerX - size.width / 2) < 12) {
            x = size.width / 2 - element.width / 2;
            showX = true;
          }
          if (Math.abs(centerY - size.height / 2) < 12) {
            y = size.height / 2 - element.height / 2;
            showY = true;
          }
          if (draft.layout.snapToGrid && !event.altKey) {
            x = Math.round(x / draft.layout.gridSize) * draft.layout.gridSize;
            y = Math.round(y / draft.layout.gridSize) * draft.layout.gridSize;
          }
          element.x = Math.max(-element.width * 0.85, Math.min(size.width - element.width * 0.15, x));
          element.y = Math.max(-element.height * 0.85, Math.min(size.height - element.height * 0.15, y));
        });
      }, false);
      setGuides({ x: showX, y: showY });
      return;
    }

    if (interaction.mode === 'rotate') {
      const angle = Math.atan2(point.y - interaction.center.y, point.x - interaction.center.x);
      let degrees = interaction.initialRotation + ((angle - interaction.startAngle) * 180) / Math.PI;
      if (event.shiftKey) degrees = Math.round(degrees / 15) * 15;
      mutate((draft) => {
        const cell = draft.cells.find((item) => item.id === activeCell.id);
        const element = cell?.elements.find((item) => item.id === interaction.id);
        if (element) element.rotation = Math.round(degrees * 10) / 10;
      }, false);
      return;
    }

    const initial = interaction.initial;
    const deltaGlobal = { x: point.x - interaction.start.x, y: point.y - interaction.start.y };
    const delta = rotatePoint(deltaGlobal.x, deltaGlobal.y, -initial.rotation);
    const hx = interaction.handle.includes('e') ? 1 : interaction.handle.includes('w') ? -1 : 0;
    const hy = interaction.handle.includes('s') ? 1 : interaction.handle.includes('n') ? -1 : 0;
    let newWidth = initial.width + delta.x * hx;
    let newHeight = initial.height + delta.y * hy;
    const preserve = ['character', 'prop', 'image'].includes(initial.kind) !== event.shiftKey;
    if (preserve && hx !== 0 && hy !== 0) {
      const scaleX = newWidth / initial.width;
      const scaleY = newHeight / initial.height;
      const scale = Math.abs(scaleX - 1) > Math.abs(scaleY - 1) ? scaleX : scaleY;
      newWidth = initial.width * scale;
      newHeight = initial.height * scale;
    }
    newWidth = Math.max(40, newWidth);
    newHeight = Math.max(30, newHeight);
    const widthDelta = newWidth - initial.width;
    const heightDelta = newHeight - initial.height;
    const localCenterShift = { x: (widthDelta / 2) * hx, y: (heightDelta / 2) * hy };
    const globalCenterShift = rotatePoint(localCenterShift.x, localCenterShift.y, initial.rotation);
    const originalCenter = { x: initial.x + initial.width / 2, y: initial.y + initial.height / 2 };
    const newCenter = { x: originalCenter.x + globalCenterShift.x, y: originalCenter.y + globalCenterShift.y };

    mutate((draft) => {
      const cell = draft.cells.find((item) => item.id === activeCell.id);
      const element = cell?.elements.find((item) => item.id === initial.id);
      if (!element) return;
      element.width = Math.round(newWidth * 10) / 10;
      element.height = Math.round(newHeight * 10) / 10;
      element.x = Math.round((newCenter.x - newWidth / 2) * 10) / 10;
      element.y = Math.round((newCenter.y - newHeight / 2) * 10) / 10;
      if (element.kind === 'text' || element.kind === 'bubble') {
        const ratio = newHeight / initial.height;
        if (event.altKey && element.textStyle) element.textStyle.fontSize = Math.max(10, initial.textStyle!.fontSize * ratio);
      }
    }, false);
  };

  const endPointer = (event: React.PointerEvent<SVGSVGElement>) => {
    const interaction = interactionRef.current;
    if (!interaction || interaction.pointerId !== event.pointerId) return;
    if (svgRef.current?.hasPointerCapture(event.pointerId)) svgRef.current.releasePointerCapture(event.pointerId);
    if (interaction.changed) finishInteraction();
    else cancelInteraction();
    interactionRef.current = null;
    setGuides({ x: false, y: false });
  };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented || document.querySelector('dialog[open], [role="dialog"], [role="menu"]') || isTypingTarget(event.target) || (event.target instanceof Element && event.target.closest('.ss-header'))) return;
      const command = event.metaKey || event.ctrlKey;
      if (command && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
        return;
      }
      if (command && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        redo();
        return;
      }
      if (command && event.key.toLowerCase() === 'c') {
        event.preventDefault(); copySelected(); return;
      }
      if (command && event.key.toLowerCase() === 'v') {
        event.preventDefault(); pasteClipboard(); return;
      }
      if (command && event.key.toLowerCase() === 'd') {
        event.preventDefault(); duplicateSelected(); return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedIds.length) {
          event.preventDefault(); deleteSelected();
        }
        return;
      }
      if (event.key === 'Escape') {
        setSelectedIds([]);
        return;
      }
      if ((event.key === '[' || event.key === ']') && selectedIds.length === 1) {
        event.preventDefault();
        moveLayer(selectedIds[0], event.key === ']' ? (event.shiftKey ? 'front' : 'forward') : (event.shiftKey ? 'back' : 'backward'));
        return;
      }
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) && selectedIds.length && activeCell) {
        event.preventDefault();
        const amount = event.shiftKey ? 10 : 1;
        const dx = event.key === 'ArrowLeft' ? -amount : event.key === 'ArrowRight' ? amount : 0;
        const dy = event.key === 'ArrowUp' ? -amount : event.key === 'ArrowDown' ? amount : 0;
        mutate((draft) => {
          const cell = draft.cells.find((item) => item.id === activeCell.id);
          cell?.elements.forEach((element) => {
            if (selectedIds.includes(element.id) && !element.locked) {
              element.x += dx;
              element.y += dy;
            }
          });
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeCell, copySelected, deleteSelected, duplicateSelected, moveLayer, mutate, pasteClipboard, redo, selectedIds, setSelectedIds, undo]);

  if (!project || !activeCell) return <div className="canvas-loading">Loading canvas…</div>;

  const multiBounds = selectedElements.length > 1 ? selectedElements.reduce((bounds, element) => ({
    minX: Math.min(bounds.minX, element.x),
    minY: Math.min(bounds.minY, element.y),
    maxX: Math.max(bounds.maxX, element.x + element.width),
    maxY: Math.max(bounds.maxY, element.y + element.height),
  }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }) : null;

  return (
    <div className="canvas-viewport" ref={viewportRef}>
      <div
        className="canvas-zoom-layer"
        style={{ width: size.width * fitScale * zoom, height: size.height * fitScale * zoom }}
      >
        <svg
          ref={svgRef}
          className="editor-canvas-svg"
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${size.width} ${size.height}`}
          width={size.width}
          height={size.height}
          onPointerMove={handlePointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget || (event.target as Element).hasAttribute('data-canvas-background')) setSelectedIds([]);
          }}
          aria-label={`Editing ${activeCell.title}`}
        >
          <g data-canvas-background="true" pointerEvents="none"><SceneArt scene={activeCell.scene} width={size.width} height={size.height} prefix={`${project.id}-${activeCell.id}-editor`} /></g>
          {elements.map((element) => (
            <g
              key={element.id}
              className={`canvas-element ${selectedIds.includes(element.id) ? 'is-selected' : ''}`}
              onPointerDown={(event) => startMove(event, element)}
              onDoubleClick={() => setSelectedIds([element.id])}
              style={{ cursor: element.locked ? 'not-allowed' : 'move' }}
              role="graphics-symbol"
              aria-label={`${element.name}${element.locked ? ', locked' : ''}`}
            >
              <ElementArt element={element} />
            </g>
          ))}

          {guides.x && <line className="alignment-guide" x1={size.width / 2} y1={0} x2={size.width / 2} y2={size.height} />}
          {guides.y && <line className="alignment-guide" x1={0} y1={size.height / 2} x2={size.width} y2={size.height / 2} />}

          {single && !single.hidden && (
            <g transform={`rotate(${single.rotation} ${single.x + single.width / 2} ${single.y + single.height / 2})`}>
              <rect className="selection-box" x={single.x} y={single.y} width={single.width} height={single.height} />
              {!single.locked && (
                <>
                  <line className="rotation-stem" x1={single.x + single.width / 2} y1={single.y - 60} x2={single.x + single.width / 2} y2={single.y} />
                  <circle className="rotation-handle" cx={single.x + single.width / 2} cy={single.y - 68} r={14} onPointerDown={(event) => startRotate(event, single)} />
                  {handles.map((handle) => (
                    <circle
                      key={handle.key}
                      className="resize-handle"
                      cx={single.x + single.width * handle.x}
                      cy={single.y + single.height * handle.y}
                      r={13}
                      style={{ cursor: handle.cursor }}
                      onPointerDown={(event) => startResize(event, single, handle.key)}
                    />
                  ))}
                </>
              )}
            </g>
          )}

          {multiBounds && (
            <rect className="selection-box multi-selection" x={multiBounds.minX} y={multiBounds.minY} width={multiBounds.maxX - multiBounds.minX} height={multiBounds.maxY - multiBounds.minY} />
          )}
        </svg>
      </div>

      <div className="zoom-controls" aria-label="Canvas zoom">
        <button type="button" className="icon-button" onClick={() => setZoom(zoom - 0.1)} aria-label="Zoom out"><Minus size={18} /></button>
        <output>{Math.round(zoom * 100)}%</output>
        <button type="button" className="icon-button" onClick={() => setZoom(zoom + 0.1)} aria-label="Zoom in"><Plus size={18} /></button>
        <button type="button" className="icon-button" onClick={() => setZoom(1)} aria-label="Fit canvas"><Maximize2 size={18} /></button>
        <button type="button" className={`icon-button ${project.layout.snapToGrid ? 'is-active' : ''}`} onClick={() => mutate((draft) => { draft.layout.snapToGrid = !draft.layout.snapToGrid; })} aria-label="Toggle snap to grid"><Grid3X3 size={18} /></button>
      </div>
      <div className="visually-hidden" aria-live="polite">
        {selectedElements.length === 0 ? 'No object selected.' : `${selectedElements.length} object${selectedElements.length === 1 ? '' : 's'} selected: ${selectedElements.map((item) => item.name).join(', ')}.`}
      </div>
    </div>
  );
};
